import { useState, useEffect, useRef } from 'react';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function NotreVision() {
  const [heroRef, heroVis] = useReveal(0.1);
  const [s1Ref, s1Vis] = useReveal(0.15);
  const [s1TxtRef, s1TxtVis] = useReveal(0.2);
  const [s2Ref, s2Vis] = useReveal(0.15);
  const [s2TxtRef, s2TxtVis] = useReveal(0.2);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[70vh] md:h-[80vh] flex items-center justify-center overflow-hidden bg-black">
        <img src="/photos/IMG_0963.jpeg" alt="La Fabrication"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.25) contrast(1.1) saturate(0.8)' }} />
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />

        <div ref={heroRef} className={`relative z-10 text-center px-6 transition-all duration-[1.5s] ${heroVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-6 mb-10">
            <div className="w-16 h-px bg-white/30" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-12 w-auto"
              style={{ filter: 'invert(1) brightness(2)', opacity: 0.5 }} />
            <div className="w-16 h-px bg-white/30" />
          </div>
          <h1 className="text-white font-light tracking-wider" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(48px, 7vw, 96px)'
          }}>
            La Fabrication
          </h1>
        </div>
      </section>

      {/* ── LA FABRICATION — Section 1 ── */}
      <section className="relative py-20 md:py-28 bg-white overflow-hidden">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 items-center gap-10 md:gap-0">
          <div ref={s1Ref}
            className={`flex justify-center transition-all duration-[1.5s] ${s1Vis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <img src="/photos/IMG_0962.jpeg" alt="Fabrication artisanale"
              className="w-[320px] md:w-[400px] lg:w-[450px] h-auto object-cover shadow-lg" />
          </div>

          <div ref={s1TxtRef}
            className={`flex items-center px-8 md:px-10 lg:px-16 transition-all duration-[1.5s] delay-300 ${s1TxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="max-w-md" style={{ fontFamily: "'Inter', sans-serif" }}>
              <h2 className="mb-8 font-normal leading-snug" style={{ color: '#2B2B2B', fontSize: 'clamp(20px, 1.8vw, 24px)' }}>
                Tout commence par une pierre,
              </h2>

              <div className="space-y-6">
                <p className="leading-[1.9] text-[15px]" style={{ color: '#7a7a7a' }}>
                  Sur les marchés et les ateliers d'Asie du Sud-Est, et surtout à Bangkok, hub mondial des pierres de couleur, haut lieu du savoir-faire lapidaire et joaillier depuis plusieurs générations.
                </p>
                <p className="leading-[1.9] text-[15px]" style={{ color: '#7a7a7a' }}>
                  Entre mon atelier parisien où je laisse courir mon imagination, et celui de mon artisan joaillier partenaire en Thaïlande, le bijou naît autour de la pierre qui l'inspire.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 2 — En toute transparence ── */}
      <section className="relative py-20 md:py-28 bg-black overflow-hidden">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 items-center gap-10 md:gap-8">
          <div ref={s2TxtRef}
            className={`flex justify-center px-6 md:px-10 lg:px-16 order-2 md:order-1 transition-all duration-[1.5s] delay-300 ${s2TxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="w-full max-w-xl" style={{ fontFamily: "'Inter', sans-serif" }}>
              <h2 className="mb-10 text-white font-light" style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(26px, 2.4vw, 34px)'
              }}>
                En toute transparence.
              </h2>

              <p className="text-white text-[16px] leading-[1.6] mb-8">
                Chez Pernety, la transparence et la confiance nous tiennent à cœur.
              </p>

              <div className="space-y-5 text-white/55 leading-[1.8] text-[13.5px]">
                <p>
                  Chaque bijou s'accompagne d'une fiche détaillant la nature de sa pierre, son origine, ainsi que les conseils pour en prendre soin.
                </p>
                <p>
                  Je sélectionne moi-même les pierres auprès de partenaires de confiance.
                </p>
                <p>
                  Les diamants, saphirs, spinelles et émeraudes de plus de 1 carat (1,02g), sont accompagnés d'un certificat d'un laboratoire indépendant.
                </p>
                <p>
                  L'or que nous utilisons est issu de circuits contrôlés. Notre démarche s'inscrit dans une volonté de responsabilité et d'amélioration continue, avec pour objectif un approvisionnement toujours plus vertueux.
                </p>
              </div>
            </div>
          </div>

          <div ref={s2Ref}
            className={`flex justify-center order-1 md:order-2 transition-all duration-[1.5s] ${s2Vis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="p-4 md:p-5 relative" style={{
              backgroundColor: '#1f1f1f',
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 300 300' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.15 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              backgroundBlendMode: 'overlay'
            }}>
              <img src="/photos/Sylvie-SMP_8.jpg" alt="Certificat — Pierres sélectionnées"
                className="w-[360px] h-[360px] md:w-[430px] md:h-[430px] lg:w-[480px] lg:h-[480px] object-cover relative" />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
